import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportFailure } from '../utils/logger';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';
import { detectarIdioma, traducir } from '../i18n/idioma';
import type { Clave } from '../i18n/textos';

/**
 * Traduce SIN el proveedor de idioma.
 *
 * Esta pantalla vive fuera de `IdiomaProvider` a propósito (ver `index.tsx`):
 * envuelve al proveedor para poder cazar también los fallos del proveedor
 * mismo. Por eso no puede usar `useIdioma()` — no hay contexto que leer.
 *
 * Pero sí puede usar `detectarIdioma()` y `traducir()`, que son funciones puras
 * sobre un diccionario estático: leen el idioma de `localStorage` y devuelven
 * texto. Sin ellas, la única pantalla que un contratista ve cuando algo se
 * rompe estaría en inglés, en una app que le promete todo en español.
 *
 * El `try` no es paranoia de manual: si lo que reventó fuera el propio módulo
 * de idioma, traducir aquí volvería a lanzar DENTRO del boundary y el usuario
 * se quedaría con la pantalla en blanco — peor que el error. El respaldo enseña
 * los dos idiomas, que es lo único que no puede fallar.
 */
function texto(clave: Clave, respaldo: string): string {
    try {
        return traducir(detectarIdioma(), clave);
    } catch {
        return respaldo;
    }
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    
    // Log the error using our global logger
    reportFailure(error, { 
        component: 'Global Error Boundary',
        context: errorInfo.componentStack ? String(errorInfo.componentStack) : undefined
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-acero-50 p-6">
          <div className="max-w-md w-full bg-white rounded-2xl border border-acero-200 p-8 text-center">
            <div className="w-10 h-10 bg-peligro-bg text-peligro rounded-xl grid place-items-center mx-auto mb-5">
                <AlertTriangle size={18} />
            </div>

            <h1 className="text-lg font-semibold tracking-tight text-navy-950 mb-1.5">
                {texto('app.error.titulo', 'Algo salió mal / Something went wrong')}
            </h1>

            <p className="text-sm text-acero-500 mb-7">
                {texto('app.error.desc', 'Ocurrió un error inesperado. / An unexpected error occurred.')}
            </p>

            <Button
                variant="secondary"
                size="lg"
                className="w-full"
                leftIcon={<RefreshCw size={18} />}
                onClick={() => window.location.reload()}
            >
                {texto('app.error.recargar', 'Recargar / Reload')}
            </Button>

            {/* El mensaje técnico, SOLO en desarrollo.
              *
              * Antes lo enseñaba a todo el mundo la otra pantalla de error —la
              * que vivía dentro de App.tsx— y se comprobó que su texto viajaba
              * en el bundle de producción: un contratista se encontraba
              * `useWorkspace must be used within a WorkspaceProvider` sin tener
              * idea de qué hacer con eso.
              *
              * Pero a quien programa sí le hace falta, y esconderlo del todo
              * obligaba a abrir la consola. `import.meta.env.DEV` lo deja fuera
              * del bundle de producción, así que aquí no cuesta nada. */}
            {import.meta.env.DEV && this.state.error && (
                <pre className="mt-6 text-left text-xs font-data text-peligro bg-peligro-bg rounded-xl p-4 whitespace-pre-wrap break-words max-h-52 overflow-y-auto">
                    {this.state.error.toString()}
                </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
